import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError, fmtTime } from "@/lib/api";
import { Panel, StatusBadge, Table } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OctagonX, RotateCcw } from "lucide-react";
import NotificationsPanel from "@/components/NotificationsPanel";
import { Switch } from "@/components/ui/switch";
import { useCelebration } from "@/context/CelebrationContext";
import { useAuth } from "@/context/AuthContext";
import { InstallAppButton } from "@/components/InstallAppButton";

const BIZ_ACCOUNT_ID = "hussar-holdings";

export default function Settings() {
  const { reloadLayout } = useOutletContext();
  const { user } = useAuth();
  const { prefs: celebPrefs, reloadPrefs, preview } = useCelebration();
  const isOwner = ["owner", "platform_owner", "admin"].includes(user?.role);
  const [s, setS] = useState(null);
  const [allocs, setAllocs] = useState([]);
  const [confirmMode, setConfirmMode] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmPaperReset, setConfirmPaperReset] = useState(false);
  const [includeInflows, setIncludeInflows] = useState(false);
  const [accountsList, setAccountsList] = useState([]);
  const [confirmRetire, setConfirmRetire] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [confirmStep, setConfirmStep] = useState(null);
  const [stepNote, setStepNote] = useState("");
  const hasBusiness = accountsList.some((a) => a.account_type === "BUSINESS");

  const completeStep = async () => {
    try {
      const r = await api.post("/admin/corporate-onboarding/complete",
        { account_id: BIZ_ACCOUNT_ID, step: confirmStep, note: stepNote });
      setOnboarding(r.data);
      toast.success(`Onboarding step completed: ${confirmStep.replaceAll("_", " ")}`);
      setConfirmStep(null); setStepNote("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const runRecon = async () => {
    setReconBusy(true);
    try {
      const r = await api.get("/admin/reconciliation-verdict");
      setRecon(r.data);
      if (r.data.status === "PASS") toast.success("RECONCILIATION: PASS — Treasury safe to proceed");
      else toast.error(`RECONCILIATION: FAIL — ${r.data.failed.length} check(s) failed`);
    } catch (e) { toast.error(apiError(e)); } finally { setReconBusy(false); }
  };

  const retireAccount = async () => {
    try {
      await api.post("/admin/retire-account", { account_id: confirmRetire, confirm: true });
      toast.success(`Account '${confirmRetire}' retired — audit history preserved (append-only)`);
      setConfirmRetire(null); load(); reloadLayout?.();
    } catch (e) { toast.error(apiError(e)); setConfirmRetire(null); }
  };

  const resetPaper = async () => {
    try {
      const r = await api.post("/admin/reset-paper-data", { confirm: true, include_inflows: includeInflows });
      const total = Object.values(r.data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(`Paper investment data reset — ${total} records removed (audited)`);
      setConfirmPaperReset(false); load(); reloadLayout?.();
    } catch (e) { toast.error(apiError(e)); setConfirmPaperReset(false); }
  };

  const load = () => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/allocations").then((r) => setAllocs(r.data)).catch(() => {});
    api.get("/accounts", { params: { account: undefined } }).then((r) => setAccountsList(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    if (isOwner && hasBusiness) {
      api.get("/admin/corporate-onboarding", { params: { account: undefined, target_account: BIZ_ACCOUNT_ID } })
        .then((r) => setOnboarding(r.data)).catch(() => {});
    }
  }, [accountsList, isOwner]);

  const changeMode = async () => {
    try {
      await api.post("/settings/mode", { mode: confirmMode, confirm: true });
      toast.success(`Execution mode → ${confirmMode.replace("_", "-")}`);
      setConfirmMode(null); load(); reloadLayout?.();
    } catch (e) { toast.error(apiError(e)); setConfirmMode(null); }
  };

  const resetKill = async () => {
    try {
      await api.post("/settings/kill-switch/reset", { confirm: true });
      toast.success("Emergency stop reset");
      setConfirmReset(false); load(); reloadLayout?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveAllocs = async () => {
    try {
      await api.put("/allocations", allocs.map(({ key, min_pct, target_pct, max_pct }) => ({ key, min_pct, target_pct, max_pct })));
      toast.success("Allocation targets updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setCeleb = async (patch) => {
    try {
      await api.put("/celebrations/settings", patch);
      reloadPrefs();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  const modes = ["PAPER", "LIVE_CONFIRM", "LIVE_AUTO"];

  return (
    <div className="space-y-5" data-testid="settings-page">
      <h2 className="font-display text-xl font-bold">Treasury Settings</h2>

      {s.kill_switch && (
        <div className="panel border-red-700 bg-red-950/40 px-4 py-4 flex items-center justify-between" data-testid="kill-active-panel">
          <div className="flex items-center gap-3">
            <OctagonX className="w-6 h-6 text-red-400" />
            <div>
              <div className="text-red-400 font-bold">EMERGENCY STOP ACTIVE</div>
              <div className="text-xs text-slate-400">All execution disabled. Monitoring, accounting and tax ledger continue.</div>
            </div>
          </div>
          <Button data-testid="kill-reset-button" onClick={() => setConfirmReset(true)} variant="outline" className="border-red-700 text-red-300">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
        </div>
      )}

      <Panel title="Execution Mode (deliberate promotion only)" testId="mode-panel">
        <div className="flex flex-wrap gap-3">
          {modes.map((m) => (
            <button key={m} data-testid={`mode-btn-${m}`} onClick={() => m !== s.execution_mode && setConfirmMode(m)}
              className={`px-4 py-3 rounded-sm border text-left min-w-[160px] transition-colors ${
                s.execution_mode === m ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2D323E] hover:border-[#3F4656]"}`}>
              <StatusBadge status={m} />
              <div className="text-[10px] text-slate-500 mt-2">
                {m === "PAPER" && "Simulated fills, real prices"}
                {m === "LIVE_CONFIRM" && "Human approves every trade"}
                {m === "LIVE_AUTO" && "Risk-Governor-gated auto execution"}
              </div>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-600 mt-3">Progression is strictly PAPER → LIVE-CONFIRM → LIVE-AUTO. Leaving PAPER requires verified Crypto.com credentials. Every change is an audit event.</p>
      </Panel>

      <NotificationsPanel />

      <Panel title="Experience — Profit Celebration" testId="celebration-settings-panel">
        <p className="text-[10px] text-slate-600 mb-4 leading-relaxed">
          Fires only on confirmed realized value recorded by the ledger — realized gains, Wildcard harvests, staking rewards,
          dividends, interest, and confirmed income. Never on unrealized market movement, deposits, or internal transfers.
          Sound identity: heavy coins deliberately secured into a treasure chest — value captured, not gambling.
        </p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="celebration-mode-selector">
          {["OFF", "SUBTLE", "HUSSAR", "JACKPOT"].map((m) => (
            <button key={m} data-testid={`celebration-mode-${m.toLowerCase()}`} onClick={() => setCeleb({ mode: m })}
              className={`px-4 py-2.5 rounded-sm border text-left min-w-[120px] transition-colors ${
                celebPrefs?.mode === m ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2D323E] hover:border-[#3F4656]"}`}>
              <div className="text-xs font-bold tracking-widest text-slate-100">{m}</div>
              <div className="text-[9px] text-slate-500 mt-1">
                {m === "OFF" && "Silent. No celebration."}
                {m === "SUBTLE" && "1–2 soft coin drops, brief"}
                {m === "HUSSAR" && "Signature chest-of-gold landing"}
                {m === "JACKPOT" && "Fuller coin cascade, treasure-themed"}
              </div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-6 mb-4">
          {[["sound", "Sound"], ["haptics", "Haptics"], ["visual", "Visual Celebration"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2.5 text-xs text-slate-300 cursor-pointer" data-testid={`celebration-toggle-${k}-label`}>
              <Switch data-testid={`celebration-toggle-${k}`} checked={!!celebPrefs?.[k]}
                onCheckedChange={(v) => setCeleb({ [k]: v })} disabled={!celebPrefs || celebPrefs.mode === "OFF"} />
              {label}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="test-celebration-button" variant="outline" disabled={!celebPrefs}
            onClick={() => preview(celebPrefs?.mode || "HUSSAR")}
            className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10">
            TEST CELEBRATION
          </Button>
          <span className="text-[10px] text-slate-600">Preview of the selected mode. No ledger event or financial record is created.</span>
        </div>
      </Panel>

      {isOwner && (
        <Panel title="Treasury Reconciliation (Platform Owner)" testId="reconciliation-panel">
          <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
            One-click verdict on whether the Treasury is safe to proceed: account roster integrity,
            NAV / contribution / tax-reserve reconciliation per account, lot and execution integrity,
            audit chain validity, genesis, and external anchor freshness. Leaving PAPER mode is
            blocked while reconciliation fails.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <Button data-testid="run-reconciliation-button" disabled={reconBusy} onClick={runRecon}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
              {reconBusy ? "RUNNING…" : "RUN RECONCILIATION"}
            </Button>
            {recon && <span className="text-[10px] text-slate-500 font-mono">ran {fmtTime(recon.generated_at)}</span>}
          </div>
          {recon && (
            <>
              <div data-testid="reconciliation-verdict"
                className={`px-4 py-3 mb-4 rounded-sm border font-bold tracking-widest text-sm ${
                  recon.status === "PASS"
                    ? "border-emerald-700 bg-emerald-950/30 text-emerald-400"
                    : "border-red-700 bg-red-950/40 text-red-400"}`}>
                RECONCILIATION: {recon.status} · MODE: {(recon.execution_mode || "").replace("_", "-")}
              </div>
              <div className="space-y-1.5">
                {recon.checks.map((c) => (
                  <div key={c.key} className="flex items-start justify-between gap-3 border-b border-[#222630] pb-1.5" data-testid={`recon-check-${c.key}`}>
                    <span className="text-xs text-slate-300">{c.label}</span>
                    <span className={`text-[10px] font-bold font-mono text-right ${c.status === "PASS" ? "text-emerald-400" : "text-red-400"}`}>
                      {c.status}{c.detail ? ` — ${c.detail}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>
      )}

      {isOwner && hasBusiness && (
        <Panel title="Corporate Onboarding — Hussar Holdings LLC" testId="corporate-onboarding-panel">
          <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
            Ordered, append-only, audit-chained steps from "business account exists" to "live provider
            eligible". Completed steps are permanent — timestamped and attributable. LIVE-CONFIRM for the
            corporate account is blocked until every step is complete.
          </p>
          {onboarding ? (
            <>
              <div className="space-y-2">
                {onboarding.steps.map((st, i) => (
                  <div key={st.key} className="flex items-center justify-between gap-3 border-b border-[#222630] pb-2" data-testid={`onboarding-step-${st.key}`}>
                    <div>
                      <div className="text-xs text-slate-200 font-semibold">{i + 1}. {st.label}</div>
                      {st.done && (
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                          {fmtTime(st.at)} · {st.by}{st.note ? ` — ${st.note}` : ""}
                        </div>
                      )}
                    </div>
                    {st.done ? (
                      <span className="text-[10px] font-bold text-emerald-400 tracking-widest shrink-0" data-testid={`onboarding-status-${st.key}`}>DONE</span>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`onboarding-complete-${st.key}`}
                        disabled={i > 0 && !onboarding.steps[i - 1].done}
                        onClick={() => { setConfirmStep(st.key); setStepNote(""); }}
                        className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 shrink-0">
                        MARK COMPLETE
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div data-testid="onboarding-eligibility"
                className={`mt-4 px-4 py-3 rounded-sm border font-bold tracking-widest text-sm ${
                  onboarding.live_confirm_eligible
                    ? "border-emerald-700 bg-emerald-950/30 text-emerald-400"
                    : "border-[#3F4656] bg-[#121316] text-slate-400"}`}>
                LIVE-CONFIRM: {onboarding.live_confirm_eligible ? "ELIGIBLE" : "NOT ELIGIBLE"}
              </div>
              {onboarding.all_steps_done && !onboarding.live_confirm_eligible && (
                <div className="mt-2 text-[10px] text-amber-300 font-mono" data-testid="onboarding-evidence-lapsed">
                  Evidence lapsed — steps remain completed historically, but eligibility is a current safety state:
                  credentials {onboarding.eligibility_evidence?.credentials_present ? "present" : "REMOVED"},
                  reconciliation {onboarding.eligibility_evidence?.reconciliation_current ? "current" : "STALE/FAILING"},
                  audit chain {onboarding.eligibility_evidence?.audit_chain_valid ? "VALID" : "INVALID"}.
                </div>
              )}
            </>
          ) : (
            <div className="text-slate-500 font-mono text-xs">Loading onboarding state…</div>
          )}
        </Panel>
      )}

      {isOwner && (
        <Panel title="Paper Investment Data Reset (Platform Owner)" testId="paper-reset-panel">
          <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
            Resets PAPER investment history for the current account only: paper positions, trades, tax lots,
            realized/unrealized P&amp;L, portfolio snapshots, deployment proposals, executions, and Wildcard state.
            Never touches users, authentication, security/audit logs, bootstrap history, configuration, or account
            ownership. Available only in PAPER mode. The reset itself is recorded in the audit chain.
          </p>
          <label className="flex items-center gap-2.5 text-xs text-slate-300 cursor-pointer mb-3" data-testid="paper-reset-inflows-label">
            <Switch data-testid="paper-reset-include-inflows" checked={includeInflows} onCheckedChange={setIncludeInflows} />
            Also remove recorded capital inflows (full clean slate — re-add the real seed afterwards)
          </label>
          <div className="flex items-center gap-3">
            <Button data-testid="paper-reset-button" variant="outline" disabled={s.execution_mode !== "PAPER"}
              onClick={() => setConfirmPaperReset(true)} className="border-red-700 text-red-300 hover:bg-red-950/40">
              RESET PAPER INVESTMENT DATA
            </Button>
            {s.execution_mode !== "PAPER" && <span className="text-[10px] text-slate-500">Disabled outside PAPER mode</span>}
          </div>
        </Panel>
      )}

      {isOwner && (
        <Panel title="Audit Genesis (This Environment)" testId="genesis-panel">
          <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
            Establishes the one-time AUDIT_PRODUCTION_GENESIS marker + signed checkpoint in this
            environment's audit chain (the real-money baseline). Idempotent — safe to click; runs once.
          </p>
          <Button data-testid="establish-genesis-button" variant="outline"
            onClick={async () => {
              try {
                const r = await api.post("/admin/establish-genesis");
                toast.success(r.data.created ? `Audit genesis established (seq ${r.data.seq})` : "Genesis already exists in this environment");
              } catch (e) { toast.error(apiError(e)); }
            }}
            className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10">
            ESTABLISH AUDIT GENESIS
          </Button>
        </Panel>
      )}

      {isOwner && (
        <Panel title="Account Administration (Platform Owner)" testId="account-admin-panel">
          <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">
            Retiring removes the account entity and its operational data only. The append-only audit chain,
            decision journal, and audit genesis are never touched — the retirement itself is recorded as an
            ACCOUNT_REMOVED audit event. Other accounts' balances and configuration are unaffected.
          </p>
          <div className="space-y-2">
            {accountsList.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b border-[#222630] pb-2" data-testid={`account-admin-row-${a.id}`}>
                <div>
                  <div className="text-sm text-slate-200">{a.name} <span className="font-mono text-xs text-slate-500">({a.id})</span></div>
                  <div className="text-[10px] text-slate-500">{a.account_type} · {a.risk_profile}</div>
                </div>
                <Button data-testid={`retire-account-button-${a.id}`} variant="outline" size="sm"
                  disabled={a.id === (localStorage.getItem("hussar_account") || "konrad")}
                  onClick={() => setConfirmRetire(a.id)}
                  className="border-red-700 text-red-300 hover:bg-red-950/40">
                  RETIRE
                </Button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-600 mt-3">The currently selected account cannot be retired while in use — switch accounts first.</p>
        </Panel>
      )}

      <Panel title="Install App" testId="install-app-panel">
        <div className="flex items-center gap-4 flex-wrap">
          <img src="/assets/hussar-mark.png" alt="Hussar Treasury emblem" className="w-11 h-11 object-contain" data-testid="install-app-icon" />
          <InstallAppButton />
          <span className="text-[10px] text-slate-500 max-w-md">Adds Hussar Treasury to your home screen / dock as a standalone app. Safe offline shell only — financial data is never cached and always loads fresh.</span>
        </div>
      </Panel>

      {s.account && (
        <Panel title={`Account — ${s.account.name}`} testId="account-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="metric-label">Type</div><div className="text-slate-200">{s.account.account_type}</div></div>
            <div><div className="metric-label">Risk Profile</div><div className="text-slate-200 gold-text font-semibold">{s.account.risk_profile}</div></div>
            <div><div className="metric-label">Beneficiary</div><div className="text-slate-200">{s.account.beneficiary_name || "—"}</div></div>
            <div><div className="metric-label">Status</div><div className="text-slate-200">{s.account.status}</div></div>
            <div><div className="metric-label">Policy Version</div><div className="text-slate-200 font-mono" data-testid="account-policy-version">v{s.policy_version ?? 1}</div></div>
          </div>
          <p className="text-[10px] text-slate-600 mt-3">Execution mode, kill switch, risk rules, allocation bands, inflow routing, wildcard policy, and notification preferences on this page apply to the currently selected account only. No setting moves capital between accounts.</p>
        </Panel>
      )}

      <Panel title="Target Allocation Bands (min / target / max %)" testId="allocations-panel">
        <Table testId="allocations-table" head={[{ label: "Category" }, { label: "Min %", num: true }, { label: "Target %", num: true }, { label: "Max %", num: true }]}>
          {allocs.map((a, i) => (
            <tr key={a.key} data-testid={`alloc-row-${a.key}`}>
              <td className="td-cell font-semibold text-slate-100">{a.name}</td>
              {["min_pct", "target_pct", "max_pct"].map((f) => (
                <td key={f} className="td-num">
                  <Input type="number" step="0.5" data-testid={`alloc-${a.key}-${f}`} value={a[f]}
                    onChange={(e) => { const c = [...allocs]; c[i] = { ...a, [f]: parseFloat(e.target.value) || 0 }; setAllocs(c); }}
                    className="w-20 ml-auto bg-[#121316] border-[#2D323E] font-mono h-7 text-xs text-right" />
                </td>
              ))}
            </tr>
          ))}
        </Table>
        <div className="mt-3 flex items-center gap-4">
          <Button data-testid="allocations-save-button" onClick={saveAllocs} className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">SAVE ALLOCATION BANDS</Button>
          <span className="text-xs text-slate-500">Targets must sum to 100%. Current: <span className="font-mono">{allocs.reduce((t, a) => t + (a.target_pct || 0), 0)}%</span></span>
        </div>
      </Panel>

      <AlertDialog open={!!confirmMode} onOpenChange={() => setConfirmMode(null)}>
        <AlertDialogContent className="bg-[#17191E] border-[#3F4656]" data-testid="mode-confirm-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-slate-100">Change execution mode to {confirmMode?.replace("_", "-")}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This is an operating-state change and will be permanently recorded in the audit ledger and decision journal.
              Hussar Treasury never transitions silently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mode-cancel" className="bg-[#1E2128] border-[#3F4656] text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="mode-confirm" onClick={changeMode} className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">CONFIRM MODE CHANGE</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent className="bg-[#17191E] border-[#3F4656]" data-testid="kill-reset-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-slate-100">Reset emergency stop?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Execution will be re-enabled according to the current execution mode. This is recorded in the audit ledger.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kill-reset-cancel" className="bg-[#1E2128] border-[#3F4656] text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="kill-reset-confirm" onClick={resetKill} className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">CONFIRM RESET</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmPaperReset} onOpenChange={setConfirmPaperReset}>
        <AlertDialogContent className="bg-[#17191E] border-red-800" data-testid="paper-reset-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 font-display">RESET PAPER INVESTMENT DATA?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This permanently deletes paper investment history for the current account (positions, trades, tax lots,
              P&amp;L, snapshots, proposals, Wildcard state){includeInflows ? " and all recorded capital inflows" : ""}.
              Users, authentication, configuration, account ownership, and the audit chain are preserved.
              The reset itself is audited. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="paper-reset-cancel" className="bg-[#1E2128] border-[#3F4656] text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="paper-reset-confirm" onClick={resetPaper}
              className="bg-red-700 hover:bg-red-600 text-white font-bold">CONFIRM RESET</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!confirmRetire} onOpenChange={() => setConfirmRetire(null)}>
        <AlertDialogContent className="bg-[#17191E] border-red-800" data-testid="retire-confirm-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 font-display">RETIRE ACCOUNT '{confirmRetire}'?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This removes the account entity and its operational data (positions, trades, tax lots, inflows,
              snapshots, Wildcard state, settings). The append-only audit chain, decision journal, and audit
              genesis are preserved — the retirement is itself recorded as an ACCOUNT_REMOVED audit event.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="retire-cancel" className="bg-[#1E2128] border-[#3F4656] text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="retire-confirm" onClick={retireAccount}
              className="bg-red-700 hover:bg-red-600 text-white font-bold">CONFIRM RETIREMENT</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!confirmStep} onOpenChange={() => setConfirmStep(null)}>
        <AlertDialogContent className="bg-[#17191E] border-[#3F4656]" data-testid="onboarding-confirm-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-slate-100">Complete step: {confirmStep?.replaceAll("_", " ")}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This is permanent and recorded in the immutable audit chain with your identity and a
              timestamp. It cannot be undone. Provider and reconciliation steps are validated against
              real backend evidence at completion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={stepNote} onChange={(e) => setStepNote(e.target.value)}
            placeholder="Note / evidence reference (optional)" data-testid="onboarding-note-input"
            className="bg-[#121316] border-[#2D323E] text-sm text-slate-200" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="onboarding-cancel" className="bg-[#1E2128] border-[#3F4656] text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="onboarding-confirm" onClick={completeStep}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">CONFIRM COMPLETION</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
